import {
  WorkplaceRequestComment,
  WorkplaceRequestCommentAttachmentInput,
  WorkplaceRequestCommentsParams,
  WorkplaceRequestCommentsResponse
} from "../types/workplace-request-comments";
import {
  UpdateSharedWorkplaceRequestParams,
  WorkplaceRequestElement,
  WorkplaceRequestUploadResponse
} from "../types/workplace-requests";
import { BaseSettingsEndpoints } from "./base";

const WORKPLACE_REQUEST_SHARING_BASE_PATH =
  "/v1/tickets/public/sharing/tickets";

/**
 * Guest access to a single shared request. Every call authenticates with the
 * share-link token set via `api.setTicketShareCode(...)`, sent as the
 * `X-Tactic-Ticket-Share-Code` header — no Tactic account required. The token
 * scopes access to exactly one request, so these methods take no ticket id.
 */
export class WorkplaceRequestSharingClient extends BaseSettingsEndpoints {
  /** Get the shared request the current share token grants access to. */
  async getSharedRequest(tz = "UTC") {
    const url = `${WORKPLACE_REQUEST_SHARING_BASE_PATH}?tz=${encodeURIComponent(tz)}`;

    return this.get<WorkplaceRequestElement>(url);
  }

  /**
   * Update the shared request. Guests may only change custom field values;
   * any other property is ignored by the backend.
   */
  async updateSharedRequest(
    params: UpdateSharedWorkplaceRequestParams,
    tz = "UTC"
  ) {
    const url = `${WORKPLACE_REQUEST_SHARING_BASE_PATH}?tz=${encodeURIComponent(tz)}`;

    return this.requestWithStatus<WorkplaceRequestElement>(
      "PATCH",
      url,
      params
    );
  }

  /** List the shared request's comments. */
  async getSharedRequestComments(params: WorkplaceRequestCommentsParams = {}) {
    const { cursor, limit, tz = "UTC" } = params;

    const queryParams = new URLSearchParams();
    queryParams.set("tz", tz);
    if (cursor) {
      queryParams.set("cursor", cursor);
    }
    if (limit) {
      queryParams.set("limit", limit.toString());
    }

    const url = `${WORKPLACE_REQUEST_SHARING_BASE_PATH}/comments?${queryParams.toString()}`;

    return this.get<WorkplaceRequestCommentsResponse>(url);
  }

  /**
   * Add a comment to the shared request as the guest. Needs text, at least one
   * attachment (uploaded through {@link generateSharedUploadUrl}), or both.
   */
  async createSharedRequestComment(
    comment: string,
    attachments?: WorkplaceRequestCommentAttachmentInput[],
    tz = "UTC"
  ) {
    const url = `${WORKPLACE_REQUEST_SHARING_BASE_PATH}/comments?tz=${encodeURIComponent(tz)}`;

    return this.post<
      {
        attachments?: WorkplaceRequestCommentAttachmentInput[];
        comment: string;
      },
      WorkplaceRequestComment
    >(url, attachments?.length ? { comment, attachments } : { comment });
  }

  /**
   * Request a signed URL to upload a file on the shared request. Upload the
   * file to `upload_url`, then submit the returned `path` — as a `file_upload`
   * field value via {@link updateSharedRequest}, or as a comment attachment
   * via {@link createSharedRequestComment}.
   */
  async generateSharedUploadUrl(filename: string) {
    const url = `${WORKPLACE_REQUEST_SHARING_BASE_PATH}/uploads`;

    return this.post<{ filename: string }, WorkplaceRequestUploadResponse>(
      url,
      {
        filename
      }
    );
  }
}
